import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Badge, Box, Button, Collapse, List, ListItem, Tooltip, Typography } from "@material-ui/core";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@material-ui/icons";

/**
 * Renders a single sidebar item, or an expandable group when `subItems`
 * is provided. Groups auto-expand when any sub-item is active, and the
 * parent is highlighted if any sub-item is active.
 *
 * When `isCollapsed` is true the sidebar is in narrow mode: we render an
 * icon-only button with a tooltip on hover and skip rendering the child
 * list entirely. Clicking a parent-with-children while collapsed will
 * request the sidebar to expand (via `onExpandSidebar`) so the user can
 * see and pick a sub-item.
 */
const DashboardSideBarNavItemComponent = ({
  icon,
  title,
  active,
  onClick,
  subItems,
  isCollapsed = false,
  onExpandSidebar,
  badgeCount = 0,
}) => {
  // Wrap the icon in a Badge when there's an unread count. Used by the
  // Support nav entry to show new-message notifications. No-op when
  // badgeCount is 0 so we don't pay the Badge DOM cost on every item.
  const iconWithBadge =
    badgeCount > 0
      ? (
          <Badge
            badgeContent={badgeCount}
            color="error"
            overlap="circular"
            max={99}
          >
            {icon}
          </Badge>
        )
      : icon;
  const hasChildren = Array.isArray(subItems) && subItems.length > 0;
  const anyChildActive = hasChildren && subItems.some((c) => c.active);
  const [expanded, setExpanded] = useState(anyChildActive);

  // Keep the group expanded whenever a child becomes active via navigation.
  useEffect(() => {
    if (anyChildActive) setExpanded(true);
  }, [anyChildActive]);

  const isActive = hasChildren ? anyChildActive : active;

  const handleParentClick = () => {
    // Collapsed + has children: expand the sidebar and open this group.
    if (isCollapsed && hasChildren) {
      if (onExpandSidebar) onExpandSidebar();
      setExpanded(true);
      return;
    }
    if (hasChildren) {
      setExpanded((v) => !v);
    } else if (onClick) {
      onClick();
    }
  };

  const parentButton = (
    <Button
      sx={{
        display: "flex",
        fontWeight: "medium",
        justifyContent: isCollapsed ? "center" : "flex-start",
        letterSpacing: 0,
        py: 1.25,
        minWidth: 0,
        textTransform: "none",
        width: "100%",
        color: isActive ? "primary.main" : "text.secondary",
        "& svg": {
          mr: isCollapsed ? 0 : 1,
        },
      }}
      onClick={handleParentClick}
    >
      {iconWithBadge}
      {!isCollapsed && (
        <Typography variant="subtitle2" textAlign="start" sx={{ flex: 1 }}>
          {title}
        </Typography>
      )}
      {!isCollapsed && hasChildren && (expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
    </Button>
  );

  return (
    <>
      <ListItem disableGutters sx={{ display: "flex", py: 0 }}>
        {isCollapsed ? (
          <Tooltip title={title} placement="right" arrow>
            <Box sx={{ width: "100%" }}>{parentButton}</Box>
          </Tooltip>
        ) : (
          parentButton
        )}
      </ListItem>
      {hasChildren && !isCollapsed && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 3 }}>
            {subItems.map((child) => (
              <ListItem
                key={child.title}
                disableGutters
                sx={{ display: "flex", py: 0 }}
              >
                <Button
                  sx={{
                    display: "flex",
                    fontWeight: "medium",
                    justifyContent: "flex-start",
                    letterSpacing: 0,
                    py: 1,
                    textTransform: "none",
                    width: "100%",
                    color: child.active ? "primary.main" : "text.secondary",
                    "& svg": {
                      mr: 1,
                      fontSize: 20,
                    },
                  }}
                  onClick={child.onClick}
                >
                  {child.icon}
                  <Typography variant="body2" textAlign="start">
                    {child.title}
                  </Typography>
                </Button>
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

DashboardSideBarNavItemComponent.propTypes = {
  icon: PropTypes.element.isRequired,
  title: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  subItems: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.element,
      title: PropTypes.string.isRequired,
      active: PropTypes.bool,
      onClick: PropTypes.func,
    })
  ),
  isCollapsed: PropTypes.bool,
  onExpandSidebar: PropTypes.func,
  badgeCount: PropTypes.number,
};

export default DashboardSideBarNavItemComponent;
